import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";
import { trackLogin } from "@/utils/analyticsTracker";
import * as logger from "@/utils/logger";

interface SubscriptionDetails {
  subscribed: boolean;
  subscription_start?: string | null;
  subscription_end?: string | null;
  reason?: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isSubscribed: boolean;
  isPremiumLoading: boolean;
  subscriptionReason: string | null;
  subscriptionDetails: SubscriptionDetails | null;
  signUp: (email: string, password: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  checkSubscriptionStatus: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isPremiumLoading, setIsPremiumLoading] = useState(true);
  const [subscriptionReason, setSubscriptionReason] = useState<string | null>(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);
  const { toast } = useToast();

  const logAuthEvent = (event: string, details?: any) => {
    const logDetails = {
      ...details,
      userPresent: !!user,
      userId: user?.id || 'No user ID',
      timestamp: new Date().toISOString()
    };
    logger.log(`AuthContext - ${event}`, logDetails);
  };

  useEffect(() => {
    // Check active session on mount
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        setSession(session);
        setUser(session?.user ?? null);
        
        logAuthEvent("Session check complete", {
          sessionExists: !!session,
          userId: session?.user?.id || 'No user ID'
        });
        
        if (session?.user) {
          logAuthEvent("User is authenticated", {
            userId: session.user.id,
            email: session.user.email
          });
          checkSubscriptionStatus();
        } else {
          logAuthEvent("No active session found");
          setIsPremiumLoading(false);
        }
      } catch (error) {
        logger.error("Error checking session:", error);
      } finally {
        setIsLoading(false);
        logAuthEvent("isLoading set to false");
      }
    };
    
    checkSession();
    
    // Track whether we've already seen a session to avoid duplicate login events
    let hadSessionOnMount = false;
    
    // Listen for auth state changes
    const { data: listener } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      logAuthEvent("Auth state changed", {
        event,
        sessionExists: !!newSession,
        userId: newSession?.user?.id || 'No user ID'
      });
      
      setSession(newSession);
      setUser(newSession?.user ?? null);
      
      logAuthEvent("State updated after auth change", {
        event,
        user: newSession?.user ? {id: newSession.user.id, email: newSession.user.email} : 'No user',
        sessionToken: newSession ? 'Present' : 'Missing'
      });
      
      if (event === "SIGNED_IN" && newSession?.user) {
        logAuthEvent("User signed in", {
          userId: newSession.user.id,
          email: newSession.user.email
        });
        // Only track login for genuine new sign-ins, not session restoration on mount
        if (hadSessionOnMount) {
          // This is a real new sign-in (user wasn't signed in before)
          trackLogin({
            userId: newSession.user.id,
            userEmail: newSession.user.email
          });
        }
        // After the first SIGNED_IN event, mark that we have a session
        hadSessionOnMount = true;
        checkSubscriptionStatus();
      } else if (event === "SIGNED_OUT") {
        logAuthEvent("User signed out");
        hadSessionOnMount = true; // Next SIGNED_IN is a real login
        setIsSubscribed(false);
        setSubscriptionReason(null);
        setSubscriptionDetails(null);
        setIsPremiumLoading(false);
      }
    });
    
    return () => {
      listener?.subscription.unsubscribe();
      logAuthEvent("Auth listener unsubscribed");
    };
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      setIsPremiumLoading(true);
      logAuthEvent("Checking subscription status");
      
      const { data, error } = await supabase.functions.invoke("check-subscription");
      
      if (error) {
        throw error;
      }
      
      logAuthEvent("Subscription status response", {
        data,
        subscribed: data.subscribed === true,
        reason: data.reason || 'No reason provided'
      });
      
      setIsSubscribed(data.subscribed === true);
      setSubscriptionReason(data.reason || null);
      setSubscriptionDetails(data);
    } catch (error: any) {
      logAuthEvent("Error checking subscription status", {
        error: error.message || 'Unknown error'
      });
      setIsSubscribed(false);
      setSubscriptionReason(`Error: ${error.message || 'Unknown error'}`);
    } finally {
      setIsPremiumLoading(false);
      logAuthEvent("isPremiumLoading set to false");
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) throw error;
      toast({
        title: "Account created successfully",
        description: "Please check your email to verify your account."
      });
    } catch (error: any) {
      toast({
        title: "Error creating account",
        description: error.message || "An error occurred during sign up.",
        variant: "destructive"
      });
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      toast({
        title: "Welcome back!",
        description: "You have successfully logged in."
      });
    } catch (error: any) {
      toast({
        title: "Login failed",
        description: error.message || "Invalid email or password.",
        variant: "destructive"
      });
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`
        }
      });
      if (error) throw error;
    } catch (error: any) {
      toast({
        title: "Login failed",
        description: error.message || "Could not log in with Google.",
        variant: "destructive"
      });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear any local state used for free access so a fresh session starts
      localStorage.removeItem("helloml_free_access_start");
      localStorage.removeItem("helloml_free_access_used");

      // Immediately drop auth state to avoid UI flash while the listener updates
      setUser(null);
      setSession(null);

      toast({
        title: "Signed out",
        description: "You have been successfully logged out."
      });
    } catch (error: any) {
      toast({
        title: "Error signing out",
        description: error.message || "An error occurred during sign out.",
        variant: "destructive"
      });
      throw error;
    }
  };

  const value = {
    user,
    session,
    isLoading,
    isSubscribed,
    isPremiumLoading,
    subscriptionReason,
    subscriptionDetails,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    checkSubscriptionStatus
  };

  logAuthEvent("Current auth state", {
    userPresent: !!user,
    userId: user?.id || 'No user ID',
    isLoading,
    isSubscribed,
    isPremiumLoading,
    subscriptionReason
  });

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  
  logger.log("useAuth hook - Auth context accessed", {
    userPresent: !!context.user,
    userId: context.user?.id || 'No user ID',
    isLoading: context.isLoading,
    isSubscribed: context.isSubscribed,
    isPremiumLoading: context.isPremiumLoading,
    subscriptionReason: context.subscriptionReason,
    timestamp: new Date().toISOString()
  });
  
  return context;
};
